import { useCallback, useRef } from "react"

/**
 * Focus-handoff for the Base-UI-menu → Radix-dialog boundary (OKS-617).
 *
 * When a `DropdownMenuItem` opens a Radix dialog/alert-dialog, the Radix dialog
 * grabs focus synchronously on open. Base UI Menu, on close, otherwise restores
 * focus to its trigger via Floating UI's `returnFocus` — which, for the deferred
 * close path, can fire after the dialog mounted and steal focus back to the
 * trigger (the OKS-615 focus-restore race, mirrored across the library
 * boundary). This hook makes the menu YIELD focus to the dialog for that one
 * close, while preserving the normal restore-to-trigger on Escape / click-away.
 *
 * Wiring at a menu→dialog site:
 *
 *   const { finalFocus, yieldFocusToDialog } = useMenuDialogHandoff()
 *   <DropdownMenuContent finalFocus={finalFocus}>
 *     <DropdownMenuItem onClick={() => { yieldFocusToDialog(); setDialogOpen(true) }}>
 *
 * `finalFocus` is a callback (Base UI treats a function `finalFocus` as an
 * explicit return-focus target): it returns `false` for the dialog-opening
 * close so the menu does not move focus, and `true` otherwise so Escape / normal
 * dismissal still returns focus to the trigger — full parity with the prior
 * Radix menu → Radix dialog behavior.
 */
export function useMenuDialogHandoff() {
  const suppressReturnRef = useRef(false)

  // Call from the menu item's onClick, immediately before opening the dialog.
  const yieldFocusToDialog = useCallback(() => {
    suppressReturnRef.current = true
  }, [])

  // Pass to <DropdownMenuContent finalFocus={...}>. Returning `false` yields
  // focus to the opening dialog; `true` restores it to the trigger.
  const finalFocus = useCallback((): boolean => {
    if (suppressReturnRef.current) {
      suppressReturnRef.current = false
      return false
    }
    return true
  }, [])

  return { finalFocus, yieldFocusToDialog }
}
